// frontend/src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import animations
import './styles/animations.css';
import './styles/print.css';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { FileProvider } from './contexts/FileContext';
import { CombinedThemeProvider } from './contexts/ThemeContext';
import { DeviceProvider } from './contexts/DeviceContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { SiteFilterProvider } from './contexts/SiteFilterContext';
import { CommandRegistryProvider } from './contexts/CommandRegistry';

// Notification components
import NotificationPrompt from './components/NotificationPrompt';
import DesktopNotificationOverlay from './components/DesktopNotificationOverlay';
import SwUpdatePrompt from './components/SwUpdatePrompt';
import OfflineBanner from './components/OfflineBanner';
import SnackbarCloseButton from './components/SnackbarCloseButton';
import notificationService from './services/notificationService';

// Layout Components (always needed)
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import PermissionRoute from './components/PermissionRoute';
import CompanyPermissionRoute from './components/CompanyPermissionRoute';
import ModulesProvider from './contexts/ModulesProvider';
import InstallPWA from './components/InstallPWA';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import SkipLink from './components/SkipLink';
import PageProgressBar from './components/PageProgressBar';
import ScrollRestoration from './components/ScrollRestoration';
import ImpersonationBannerWrapper from './components/ImpersonationBannerWrapper';
import BrandedLoader from './components/BrandedLoader';

// Eagerly-loaded critical pages — first paint + auth flow
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AuthError from './pages/AuthError';
import PermissionDenied from './pages/PermissionDenied';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import Health from './pages/Health';

// Lazy-loaded pages. Splitting these out of the initial bundle keeps
// the first paint under 1MB; heavy deps (leaflet, reactflow, xlsx) only
// download when the route is visited. `InterventionDetailResponsive`
// exports default — match that name through `.then(m => ...)` if the
// export shape ever changes.
const Settings = lazy(() => import('./pages/Settings'));
const TicketsManagement = lazy(() => import('./pages/TicketsManagement'));
const QuarantineQueue = lazy(() => import('./pages/TicketsManagement/QuarantineQueue'));
const LocationManagement = lazy(() => import('./pages/LocationManagement/index'));
const DeviceManagement = lazy(() => import('./pages/DeviceManagement/indexOptimized'));
// Fleet-wide device outage / health overview (spec 20260625-device-health-visualization).
const DeviceStatusDashboard = lazy(() => import('./components/DeviceStatusDashboard'));
// Camera's als eerstelijns toestel (spec 20260831-cameras-als-toestel, fase 1).
const Cameras = lazy(() => import('./pages/Cameras/index'));
// Meldingsgeluiden (spec 20260831-domotica-meldingen-bewonerstablet, fase 2 / T-2.12).
const NotificationSounds = lazy(() => import('./pages/NotificationSounds/index'));
// Verify/correct locations.data.phoneNumber per site (what every alarm flow dials).
const LocationPhoneNumbers = lazy(() => import('./pages/LocationPhoneNumbers/index'));
const Branding = lazy(() => import('./pages/Branding/index'));
const ChecklistTemplates = lazy(() => import('./pages/ChecklistTemplates/index'));
const ResidentOnboarding = lazy(() => import('./pages/ResidentOnboarding/index'));
const ResidentOffboarding = lazy(() => import('./pages/ResidentOffboarding/index'));
const IntakeForms = lazy(() => import('./pages/IntakeForms/index'));
const PrintProfiles = lazy(() => import('./pages/PrintProfiles/index'));
const CandidateCleanup = lazy(() => import('./pages/CandidateCleanup/index'));
const IntakeConduct = lazy(() => import('./pages/IntakeConduct/index'));
const Intakes = lazy(() => import('./pages/Intakes/index'));
const UserManagement = lazy(() => import('./pages/UserManagement/index'));
const LocationTenancy = lazy(() => import('./pages/Admin/LocationTenancy/index'));
const AuditTrail = lazy(() => import('./pages/AuditTrail'));
// Knowledge Base (spec 20260706-portal-knowledge-base, Phase 1 read path) —
// replaces the former Help.js stub at /help. Reading is auth-gated (any role);
// the authoring console is additionally gated on `knowledge_base.manage`.
const KnowledgeBaseHome = lazy(() => import('./pages/KnowledgeBase'));
// Paid module entitlements (spec 20260818-invoicingsystem/spec-00-modules.md §6).
// Neither page is module-gated: the admin screen is how a module gets enabled in the
// first place, and the settings screen must be able to report the absence of every one.
const ModulesAdmin = lazy(() => import('./pages/Admin/Modules'));
const SettingsModules = lazy(() => import('./pages/Settings/Modules'));
// Resident invoicing (spec 20260818-invoicingsystem spec-01). Phase 2 = configuration only.
const InvoicingConfig = lazy(() => import('./pages/Invoicing/Config'));
const InvoicingItems = lazy(() => import('./pages/Invoicing/Items'));
const InvoicingList = lazy(() => import('./pages/Invoicing'));
const InvoiceDetail = lazy(() => import('./pages/Invoicing/InvoiceDetail'));
const InvoicingPayments = lazy(() => import('./pages/Invoicing/Payments'));
// Cafeteria POS administration (spec 20260818-invoicingsystem spec-02 §7). Six routes,
// one tabbed screen: every tab administers the same site, so they share one site picker.
// The till itself is packages/pos-app and is not routable from the portal.
const PosCatalog = lazy(() => import('./pages/Pos/Catalog'));
// PUBLIC — outside the authenticated shell. See the route registration below.
const PosPublicStatement = lazy(() => import('./pages/Pos/PublicStatement'));
const PosConfig = lazy(() => import('./pages/Pos/Config'));
const PosRestrictions = lazy(() => import('./pages/Pos/Restrictions'));
const PosTerminals = lazy(() => import('./pages/Pos/Terminals'));
const PosDayCloses = lazy(() => import('./pages/Pos/DayCloses'));
const PosReports = lazy(() => import('./pages/Pos/Reports'));
const KbPage = lazy(() => import('./pages/KnowledgeBase/KbPage'));
const KbStatus = lazy(() => import('./pages/KnowledgeBase/KbStatus'));
const KbAdmin = lazy(() => import('./pages/KnowledgeBase/KbAdmin'));
const ReleaseNotesViewer = lazy(() => import('./pages/ReleaseNotesViewer'));
const SiteAlarmBoard = lazy(() => import('./pages/SiteAlarmBoard'));
const DeveloperInfo = lazy(() => import('./pages/DeveloperInfo'));
const CameraRequests = lazy(() => import('./pages/CameraRequests/index'));
const RequestDetail = lazy(() => import('./pages/CameraRequests/RequestDetail'));
const CompanyManagement = lazy(() => import('./pages/CompanyManagement/index'));
// Customer onboarding + the customer-facing counterpart of /company-management
// (spec 20260819-registrationandonboarding).
const Organisation = lazy(() => import('./pages/Organisation/index'));
const OnboardingForm = lazy(() => import('./pages/Onboarding/index'));
const OnboardingStatus = lazy(() => import('./pages/Onboarding/Status'));
const OnboardingActivate = lazy(() => import('./pages/Onboarding/Activate'));
// Self-service password recovery — public. Added with e-mail+password sign-in: without it
// a customer who forgets a password has no route back, since Microsoft no longer owns
// recovery for every account.
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const OnboardingAdmin = lazy(() => import('./pages/Admin/Onboarding/index'));
const FieldInterventions = lazy(() => import('./pages/FieldInterventions/index'));
const InterventionDetail = lazy(() => import('./pages/FieldInterventions/InterventionDetailResponsive'));
const CreateIntervention = lazy(() => import('./pages/FieldInterventions/CreateIntervention'));
// Shop / device catalog (spec 20260702-repair-estimates-catalog, Phase 1).
const Shop = lazy(() => import('./pages/Shop'));
const EstimatesOverview = lazy(() => import('./pages/Estimates'));
// Portal Ideabox (spec 20260817-portal-ideabox). Board · Roadmap · My ideas · Triage
// are TABS inside one page, so `/ideas` is a single registry entry with one `read` verb.
// Agenda (BASE tier) — spec 20260825-animatie-activiteiten-en-agenda §8.
// NOT module-gated: the agenda is base. /activities (paid) is a separate page.
const Agenda = lazy(() => import('./pages/Agenda'));
// Activities (PAID module `activities`) — spec §8. ModuleGate lives INSIDE these
// pages: the module question and the permission question are separate gates, and the
// route guard can only answer the second.
const Activities = lazy(() => import('./pages/Activities'));
const ActivitySettings = lazy(() => import('./pages/Activities/Settings'));

// Plaatsbeschrijvingen (PAID module `inspections`) — spec 20260901-bewoner-onboarding.
// ModuleGate lives INSIDE these pages, not on the route: a module refusal and a permission
// refusal are different customer conversations and PermissionRoute can only express the
// second.
const Inspections = lazy(() => import('./pages/Inspections'));
const InspectionDetail = lazy(() => import('./pages/Inspections/Detail'));
const InspectionCapture = lazy(() => import('./pages/Inspections/Capture'));
const InspectionTemplates = lazy(() => import('./pages/Inspections/Templates'));
const Ideas = lazy(() => import('./pages/Ideas'));
const IdeaDetail = lazy(() => import('./pages/Ideas/Detail'));
// Documentclassificatie (spec 20260902-documentclassificatie, T-5a.35 / PD-9): the classify work list.
const Documents = lazy(() => import('./pages/Documents'));
const DocumentsGrants = lazy(() => import('./pages/Documents/Grants'));
const DocumentsExclusions = lazy(() => import('./pages/Documents/Exclusions'));
// 5c: the archiving page — retention proposals, legal hold, audited batch deletion (documents.retention).
const DocumentsArchive = lazy(() => import('./pages/Documents/Archive'));
const Notifications = lazy(() => import('./pages/Notifications'));
const NotificationManagement = lazy(() => import('./pages/NotificationManagement'));
const IntegrationManagement = lazy(() => import('./pages/IntegrationManagement'));
const EmergencyServicesManagement = lazy(() => import('./pages/EmergencyServicesManagement'));
const CommanderList = lazy(() => import('./components/Integrations/TwoN/CommanderList'));
const FirmwareManagement = lazy(() => import('./pages/FirmwareManagement'));
const AlarmsDashboard = lazy(() => import('./pages/AlarmsDashboard'));
const AlarmDetails = lazy(() => import('./pages/AlarmDetails'));
// T036/T037 — friendly pages for the public bearer-token error states.
// Spec: specs/20260428-secure-public-alarm-access/plan.md §5.2.
const AlarmLinkExpired = lazy(() => import('./pages/AlarmDetails/AlarmLinkExpired'));
const AlarmFinished = lazy(() => import('./pages/AlarmDetails/AlarmFinished'));
const AlarmNotFound = lazy(() => import('./pages/AlarmDetails/AlarmNotFound'));
const AlarmError = lazy(() => import('./pages/AlarmDetails/AlarmError'));

// Public estimate-approval page + friendly pages (spec 20260702-repair-estimates-catalog, Phase 3).
const EstimateApproval = lazy(() => import('./pages/EstimateApproval'));
const EstimateApprovalExpired = lazy(() => import('./pages/EstimateApproval/EstimateApprovalExpired'));
const EstimateApprovalNotFound = lazy(() => import('./pages/EstimateApproval/EstimateApprovalNotFound'));
const EstimateApprovalError = lazy(() => import('./pages/EstimateApproval/EstimateApprovalError'));
// Public ticket "mark as solved" via email link (spec 20260625-ticket-email-reply).
const TicketSolveLanding = lazy(() => import('./pages/TicketSolve/TicketSolveLanding'));
const TicketSolveOutcome = lazy(() => import('./pages/TicketSolve/TicketSolveOutcome'));
const AlarmFlows = lazy(() => import('./pages/AlarmFlows'));
const AlarmFlowEditor = lazy(() => import('./pages/AlarmFlows/AlarmFlowEditor'));
const AlarmFlowBindings = lazy(() => import('./pages/AlarmFlows/AlarmFlowBindings'));
// IndoorMapping pulls in the full Leaflet bundle via `leaflet/dist/leaflet.css`,
// so we load the CSS lazily inside the page module instead of at app boot.
const IndoorMapping = lazy(() => import('./pages/IndoorMapping'));
const IndoorNavigationPreview = lazy(() => import('./pages/IndoorNavigationPreview'));
const ServiceManagement = lazy(() => import('./pages/ServiceManagement'));
const DeviceTypeManagement = lazy(() => import('./pages/DeviceTypeManagement'));
const DeviceLinking = lazy(() => import('./pages/DeviceLinking'));
const BaseIPManagement = lazy(() => import('./pages/BaseIPManagement'));
const BusinessUpdate = lazy(() => import('./pages/BusinessUpdate'));
const SalesPresentation = lazy(() => import('./pages/SalesPresentation'));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const Observability = lazy(() => import('./pages/Observability'));
const Troubleshooting = lazy(() => import('./pages/Troubleshooting'));
const HubStatus = lazy(() => import('./pages/HubStatus'));
const HubLocationStatus = lazy(() => import('./pages/HubLocationStatus'));
const PersonsManagement = lazy(() => import('./pages/PersonsManagement'));
const PersonTypeManagement = lazy(() => import('./pages/PersonTypeManagement'));
const EnOceanButtonManagement = lazy(() => import('./pages/EnOceanButtonManagement'));
const EnOceanButtonImport = lazy(() => import('./pages/EnOceanButtonImport'));
const LocationDataImport = lazy(() => import('./pages/LocationDataImport'));
const UnitAudit = lazy(() => import('./pages/UnitAudit'));
const DatabaseHealth = lazy(() => import('./pages/DatabaseHealth'));
const QbusOutputTypeManagement = lazy(() => import('./pages/QbusOutputTypeManagement'));
const QbusPrefixes = lazy(() => import('./pages/QbusPrefixes'));
const Automations = lazy(() => import('./pages/Automations'));
const QbusFriendlyNames = lazy(() => import('./pages/QbusFriendlyNames'));
const QbusMonitor = lazy(() => import('./pages/QbusMonitor'));
const TelephonyManagement = lazy(() => import('./pages/TelephonyManagement'));
const TabletPermissions = lazy(() => import('./pages/TabletPermissions'));
const EinkDesigner = lazy(() => import('./pages/EinkDesigner'));

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  useEffect(() => {
    // Initialize notification service
    notificationService.initialize();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CombinedThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <SnackbarProvider
            // Raised from 3 → 5 so a burst of alarm-related toasts
            // doesn't silently drop earlier messages. Still low enough
            // that the stack doesn't dominate the screen.
            maxSnack={5}
            // Collapse repeat messages (e.g. "Toggled" × 3) into one.
            preventDuplicate
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            // Lift snackbars above the fixed mobile bottom nav + iOS
            // home-indicator so users can still read & dismiss them.
            // On md+ the regular bottom-right position is unchanged.
            classes={{ containerRoot: 'caro-snackbar-root' }}
            // Default dismiss button on every snackbar. Snackbars that
            // supply their own `action` override this (notistack
            // precedence), so Undo / Reload-app toasts keep their own UX.
            action={(key) => <SnackbarCloseButton snackbarKey={key} />}
          >
            <AuthProvider>
              <PermissionProvider>
                <FileProvider>
                  <DeviceProvider>
                    <NavigationProvider>
                      <SiteFilterProvider>
                      <CommandRegistryProvider>
                      <ModulesProvider>
                      <ErrorBoundary>
                      <Router>
                        <SkipLink />
                        <PageProgressBar />
                        <ScrollRestoration />
                        {/* Impersonation Banner (shows when admin is viewing as another user) */}
                        <ImpersonationBannerWrapper />

                        {/* PWA Install Prompt */}
                        <InstallPWA />

                        {/* Service-worker update toast */}
                        <SwUpdatePrompt />

                        {/* Offline banner */}
                        <OfflineBanner />

                        {/* Notification Permission Prompt */}
                        <NotificationPrompt />

                        {/* Desktop Notification Overlay (for PWA) */}
                        <DesktopNotificationOverlay />

                        <Suspense fallback={
                          <BrandedLoader label="Loading…" sx={{ minHeight: '60vh' }} />
                        }>
                        <RouteErrorBoundary>
                        <Routes>
                          {/* Public routes */}
                          <Route path="/login" element={<Login />} />
                          <Route path="/health" element={<Health />} />
                          <Route path="/reset-password" element={<PasswordReset />} />

                          {/* Public alarm access via UUID — bearer-token guarded.
                              The token rides in the URL fragment (`#t=<hex>`) and
                              is read by `usePublicAlarmToken` inside <AlarmDetails>.
                              Spec: specs/20260428-secure-public-alarm-access/plan.md §5.2.
                              Friendly error routes — registered BEFORE `:uuid` so the
                              literal paths match first. T033/T036/T037. */}
                          <Route path="/alarm/expired" element={<AlarmLinkExpired />} />
                          <Route path="/alarm/finished" element={<AlarmFinished />} />
                          <Route path="/alarm/not-found" element={<AlarmNotFound />} />
                          <Route path="/alarm/error" element={<AlarmError />} />
                          <Route path="/alarm/:uuid" element={<AlarmDetails />} />

                          {/* The monthly cafeteria statement, from the link in the site's
                              mail — PUBLIC, and deliberately so. The recipient is
                              `site_manager_email` or a shared ticket inbox, and there is no
                              promise anybody behind it has a portal account; a login would
                              be a second obstacle after the missing attachment.

                              Same shape as the alarm link above: an HMAC bearer token in the
                              fragment (`#t=<hex>`), read by `usePublicFragmentToken` inside
                              the page and handed to the API as `Authorization: Bearer`. The
                              token is never in a query string, because the document is a
                              month of per-apartment consumption. */}
                          <Route
                            path="/kassa-overzicht/:siteId/:period"
                            element={<PosPublicStatement />}
                          />

                          {/* Customer onboarding — PUBLIC, outside the authenticated shell.
                              The invite / status / activation token IS the authorisation;
                              there is no session at any of these three points. The literal
                              /onboarding/status path is registered BEFORE /onboarding so it
                              cannot be swallowed by the form route.
                              Spec: specs/20260819-registrationandonboarding/spec-01 §9. */}
                          <Route path="/onboarding/status/:token" element={<OnboardingStatus />} />
                          <Route path="/onboarding" element={<OnboardingForm />} />
                          <Route path="/activate/:token" element={<OnboardingActivate />} />

                          {/* Public estimate approval via UUID — bearer-token guarded.
                              The approval token rides in the URL fragment (`#t=<hex>`)
                              and is read by `usePublicFragmentToken('estimate', uuid)`
                              inside <EstimateApproval>. Friendly error routes are
                              registered BEFORE `:uuid` so the literal paths match first.
                              Spec: specs/20260702-repair-estimates-catalog/ Phase 3, T045. */}
                          <Route path="/approve/expired" element={<EstimateApprovalExpired />} />
                          <Route path="/approve/not-found" element={<EstimateApprovalNotFound />} />
                          <Route path="/approve/error" element={<EstimateApprovalError />} />
                          <Route path="/approve/:uuid" element={<EstimateApproval />} />

                          {/* Site alarm board — corridor wall panel
                              (spec 20260804-site-alarm-board §4.1). PUBLIC in the
                              sense of "no portal user": registered here, OUTSIDE the
                              <PrivateRoute><Layout> nesting below, so the panel gets
                              no sidebar, no header and no session redirect. It is not
                              unauthenticated — the page holds a provisioned device
                              token (fragment `#t=<hex>`, localStorage) and exchanges a
                              site code for a 15-minute session held in memory only.
                              No literal sub-paths today; if any are added they go
                              ABOVE this param route. */}
                          <Route path="/board/:siteUuid" element={<SiteAlarmBoard />} />

                          {/* Public ticket "mark as solved" via email link
                              (spec 20260625-ticket-email-reply). The landing is
                              side-effect-free (AC-4); outcome pages are literal
                              paths registered before it. */}
                          <Route path="/tickets/solve/done" element={<TicketSolveOutcome outcome="done" />} />
                          <Route path="/tickets/solve/expired" element={<TicketSolveOutcome outcome="expired" />} />
                          <Route path="/tickets/solve/used" element={<TicketSolveOutcome outcome="used" />} />
                          <Route path="/tickets/solve/not-found" element={<TicketSolveOutcome outcome="not-found" />} />
                          <Route path="/tickets/solve/error" element={<TicketSolveOutcome outcome="error" />} />
                          <Route path="/tickets/solve" element={<TicketSolveLanding />} />
                          {/* T033 — legacy `/alarms/uuid/:uuid` alias deleted in
                              the hard-cutover deploy (plan §9.3, Clarification #10). */}

                          {/* Auth Callback Routes */}
                          <Route path="/auth-callback" element={<AuthCallback />} />
                          <Route path="/auth-error" element={<AuthError />} />
                          <Route path="/permission-denied" element={<PermissionDenied />} />

                          {/* Private routes */}
                          <Route element={<PrivateRoute />}>
                            {/* ── Het intakegesprek staat BUITEN de portaallayout ──────
                                Eén vraag per scherm, schermvullend. Een zijbalk, een
                                kruimelpad en een zoekbalk eromheen vragen aandacht die
                                naar de bewoner hoort te gaan — het scherm wordt tijdens
                                het gesprek naar hem toe gedraaid.

                                `intake.medical_read` gate deze route NIET: zonder die
                                sleutel opent het scherm gewoon en blijven de medische
                                antwoorden afgeschermd; de server levert ze al zonder
                                waarde. Een veld helemaal verbergen laat iemand denken dat
                                er niets gevraagd is. */}
                            <Route element={<PermissionRoute
                              permission="intake.conduct"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/intake/:uuid/conduct" element={<IntakeConduct />} />
                            </Route>

                            <Route element={<Layout />}>
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                              <Route path="/dashboard" element={<Dashboard />} />


                            <Route element={<PermissionRoute
                              permission="locations.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/locations" element={<LocationManagement />} />
                            </Route>

                            {/* Devices is now permission-gated only, no longer company-1 only.
                              *
                              * Spec: specs/20260825-locations-doorklikken-naar-devices §4.6,
                              * phase 5. The drill-through from Location details targets this
                              * page, and the Devices tab it starts from is reachable on
                              * `locations.view` alone — so with the company gate in place the
                              * link landed most tenants on /permission-denied.
                              *
                              * ⚠ THIS GATE WAS NEVER AN ACCESS CONTROL. It is a React route
                              * guard; /api/devices/* answers any valid token regardless of what
                              * this file renders. What actually protects other tenants' devices
                              * is the tenant scoping on all 20 by-id handlers
                              * (specs/20260825-device-byid-tenant-scoping), which must be LIVE
                              * before this ships — removing the guard does not create exposure,
                              * it makes the page that reveals it reachable.
                              *
                              * Who this opens the page to is decided by `devices.view`, which is
                              * stamped per user, not inherited: 72 non-CARO accounts hold it
                              * today, including 60 plain `user` accounts that were stamped from
                              * an older template. The owner is adjusting those grants by hand.
                              */}
                            <Route element={<PermissionRoute
                              permission="devices.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/devices" element={<DeviceManagement />} />
                              <Route path="/devices/health" element={<DeviceStatusDashboard />} />
                            </Route>

                            {/* Camera's (spec 20260831-cameras-als-toestel).
                                Gate op `cameras.view` alleen: `cameras.edit`,
                                `cameras.manage` en de PROTECTED `cameras.manage_links`
                                bepalen wat de pagina je LAAT DOEN, en ze hier eisen zou
                                de pagina verbergen voor de meelezende medewerker voor wie
                                ze bedoeld is. De server gate per route op alle vier.

                                Bewust NIET achter `camera_requests.view`: dat is de
                                goedkeuringsstroom voor OPGENOMEN beeld. Ander
                                verwerkingsdoel, andere sleutel (OQ-4). */}
                            <Route element={<PermissionRoute
                              permission="cameras.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/cameras" element={<Cameras />} />
                            </Route>

                            {/* Meldingsgeluiden (spec
                                20260831-domotica-meldingen-bewonerstablet, fase 2).
                                Gate op `resident_notifications.view` alleen: `.edit`
                                (uploaden) en de PROTECTED `.manage` (verwijderen) bepalen
                                wat de pagina je LAAT DOEN, en ze hier eisen zou de pagina
                                verbergen voor wie hem alleen hoort te kunnen beluisteren.
                                De server gate per route op alle drie.

                                Geen van deze sleutels staat in ROLE_DEFAULT_PERMISSIONS of
                                in een permissietemplate — role defaults zijn een stamp, en
                                een uitgebreide template verspreidt zich bij de volgende
                                apply naar élke bestaande klant. */}
                            <Route element={<PermissionRoute
                              permission="resident_notifications.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/notification-sounds" element={<NotificationSounds />} />
                            </Route>

                            {/* Location phone numbers: the data.phoneNumber every alarm
                                flow dials, checked against sip_endpoints per site. One key
                                for read and write - the overview alone lists the number of
                                every apartment. Granted to no role by default. */}
                            <Route element={<PermissionRoute
                              permission="locations.phone_numbers"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/location-phone-numbers" element={<LocationPhoneNumbers />} />
                            </Route>

                            {/* Huisstijl (spec 20260903-onboarding-maatwerk / spec-04).
                                Een sleutel voor beide sporten van de cascade; de
                                scope-controle zit in de handler. Aan geen enkele rol
                                standaard toegekend: wie hier iets wijzigt, wijzigt wat
                                er op de kassa en op 800+ bewonerstablets staat. */}
                            <Route element={<PermissionRoute
                              permission="branding.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/branding" element={<Branding />} />
                            </Route>

                            {/* Intrede-checklist (spec 20260903-onboarding-maatwerk / spec-02).
                                Eigen sleutel, want EEN bewerking verandert elke toekomstige
                                intrede van de organisatie. De rijen van een LOPENDE intrede
                                staan op persons.edit -- dat is het verschil tussen de lijst
                                afwerken en de lijst herschrijven. */}
                            <Route element={<PermissionRoute
                              permission="resident_onboarding.templates"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/checklist-templates" element={<ChecklistTemplates />} />
                            </Route>

                            {/* De intredewizard (20260901-bewoner-onboarding, T-3.10..T-3.21).
                                Op persons.create, want stap (1) maakt de bewoner aan en dat
                                is de handeling waar de hele flow om draait. De API-routes
                                dragen hun eigen checkPermission -- deze bewaking vervangt
                                die niet, ze voorkomt alleen dat iemand op een scherm belandt
                                waar elke knop 403 geeft. */}
                            <Route element={<PermissionRoute
                              permission="persons.create"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/persons/onboard" element={<ResidentOnboarding />} />
                            </Route>

                            {/* De uittrede (offboarding.md LD-14). EIGEN sleutel en niet
                                persons.edit: afsluiten ontkoppelt facturatie, trekt een
                                tablet in en sluit een hoofdstuk dat alleen met de hand
                                heropend kan worden. Wie een bewoner mag inschrijven,
                                beslist daarmee niet dat die er niet meer woont. */}
                            <Route element={<PermissionRoute
                              permission="residents.offboard"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/persons/offboard" element={<ResidentOffboarding />} />
                            </Route>

                            {/* Intakeformulieren (spec 20260903-onboarding-maatwerk / spec-03).
                                Bouwen staat op intake.templates; PUBLICEREN op intake.publish,
                                en dat is een eigen handeling -- bewerken raakt niets,
                                publiceren bepaalt wat elk volgend gesprek vraagt. */}
                            <Route element={<PermissionRoute
                              permission="intake.templates"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/intake-forms" element={<IntakeForms />} />
                            </Route>

                            {/* Afdrukken naar derden (spec-04 Deel B).
                                Op intake.templates: een profiel bepaalt welke velden naar
                                buiten gaan, en dat is dezelfde soort beslissing als bepalen
                                welke vragen gesteld worden -- ze raakt elke toekomstige
                                afdruk van de organisatie. AFDRUKKEN zelf staat op
                                intake.view; de twee sloten bepalen wat er op het papier
                                komt, niet de permissie. */}
                            <Route element={<PermissionRoute
                              permission="intake.templates"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/print-profiles" element={<PrintProfiles />} />
                            </Route>

                            {/* Kandidaten opruimen (spec-02 O-6, eigenaar 2026-09-07).
                                LEZEN op persons.view: de lijst toont namen, ouderdom en
                                AANTALLEN, nooit de antwoorden zelf. Het WISSEN zit achter
                                intake.medical_read, en dat staat op de route in de API --
                                wie beslist dat zorgdata weg mag, hoort tot die gegevens
                                gerechtigd te zijn geweest. */}
                            <Route element={<PermissionRoute
                              permission="persons.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/candidate-cleanup" element={<CandidateCleanup />} />
                            </Route>

                            {/* Het gesprek zelf. `intake.medical_read` gate deze route NIET:
                                zonder die sleutel opent het scherm gewoon en blijven de
                                medische antwoorden afgeschermd -- de server levert ze al
                                zonder waarde. Een veld helemaal verbergen laat iemand denken
                                dat er niets gevraagd is. */}

                            {/* De lijst waar een gesprek begint. Tot 2026-09-09 bestond die
                                niet, en was `/intake/:uuid/conduct` dus alleen bereikbaar
                                als je het uuid al had — wat niemand had, want er was geen
                                scherm dat er een aanmaakte.

                                `intake.view` en NIET `.conduct`: wie de intakes mag inzien,
                                hoort de lijst te kunnen openen. De startknop is een aparte
                                handeling en de server weigert `POST /intakes` zonder
                                `intake.conduct`. Zo komt de routebewaking overeen met de
                                read-verb in PAGE_REGISTRY, en toont het menu de ingang aan
                                precies wie hem kan gebruiken. */}
                            <Route element={<PermissionRoute
                              permission="intake.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/intake" element={<Intakes />} />
                            </Route>

                            {/* Agenda (spec 20260825-animatie-activiteiten-en-agenda §8).
                                Gated on `agenda.view` only: `agenda.manage` decides
                                what the page lets you DO, and requiring it to SEE the
                                page would hide the agenda from the read-only staff it
                                exists for. The server enforces both keys per route.

                                Deliberately NOT behind ModuleGate. The agenda is BASE
                                (LD-1) — the paid `activities` module gates signups and
                                prices on /activities, and the `agenda` BUILDING FEATURE
                                FLAG (default TRUE) gates the TABLET. Three gates, three
                                surfaces, three different questions. */}
                            <Route element={<PermissionRoute
                              permission="agenda.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/agenda" element={<Agenda />} />
                            </Route>

                            {/* Activities (spec 20260825-animatie-activiteiten-en-agenda §8).
                                Gated on `activities.view` — the narrowest key that makes
                                the page worth rendering. The other four (`manage`,
                                `roster`, `enroll`, `pricing`) gate CONTROLS inside it,
                                and the server enforces each per endpoint.

                                ⚠ The MODULE gate is not here. It lives in ModuleGate
                                inside the pages, because a module refusal and a
                                permission refusal are different customer conversations —
                                one is a sales question, the other a rights question —
                                and PermissionRoute can only express the second.

                                /activities/settings is mounted ABOVE /activities so the
                                literal segment is matched first, the same ordering rule
                                the API router follows. */}
                            <Route element={<PermissionRoute
                              permission="activities.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/activities/settings" element={<ActivitySettings />} />
                              <Route path="/activities" element={<Activities />} />
                            </Route>

                            {/* Plaatsbeschrijvingen (spec 20260901-bewoner-onboarding §8).

                                Gated on `inspections.view` — the narrowest key that makes
                                the page worth rendering. `perform`, `publish`, `templates`
                                and `export` gate CONTROLS inside, and the server enforces
                                each per endpoint. `publish` is deliberately NOT implied by
                                `perform`: publication freezes the document permanently and a
                                correction afterwards is a new report.

                                ⚠ The MODULE gate is not here, same as activities above: it
                                lives in ModuleGate inside the pages, because "your
                                organisation has not bought this" and "you lack a permission"
                                are different conversations.

                                ORDER: the two literal segments (/templates) and the deeper
                                /:uuid/capture are mounted ABOVE /:uuid, so a literal is
                                matched before the parameter swallows it — the same ordering
                                rule the API router follows for /:uuid/comparison. */}
                            <Route element={<PermissionRoute
                              permission="inspections.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/inspections/templates" element={<InspectionTemplates />} />
                              <Route path="/inspections/:uuid/capture" element={<InspectionCapture />} />
                              <Route path="/inspections/:uuid" element={<InspectionDetail />} />
                              <Route path="/inspections" element={<Inspections />} />
                            </Route>

                            {/* Portal Ideabox (spec 20260817-portal-ideabox §8).
                                `ideas.view` only — the Triage tab gates itself on
                                `ideas.triage`, which is a PROTECTED permission that
                                the FULL_ACCESS_ROLES bypass deliberately does NOT
                                grant. Gating the whole route on triage would hide the
                                board from the people it exists for. */}
                            <Route element={<PermissionRoute
                              permission="ideas.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/ideas" element={<Ideas />} />
                              <Route path="/ideas/:ref" element={<IdeaDetail />} />
                            </Route>

                            {/* Paid module administration (spec 20260818-invoicingsystem
                                spec-00 §6). `modules.manage` is PROTECTED — the
                                FULL_ACCESS_ROLES bypass deliberately does NOT grant it,
                                so a customer's own company_admin cannot switch on a
                                bijbetalende module for themselves (D25). */}
                            <Route element={<PermissionRoute
                              permission="modules.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/admin/modules" element={<ModulesAdmin />} />
                            </Route>
                            <Route element={<PermissionRoute
                              permission="modules.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/settings/modules" element={<SettingsModules />} />
                            </Route>

                            {/* Resident invoicing — configuration tier (Phase 2).
                                Module + permission are two independent gates and the
                                server enforces both; PermissionRoute here is only the
                                affordance half. */}
                            <Route element={<PermissionRoute
                              permission="invoicing.config"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/invoicing/config" element={<InvoicingConfig />} />
                            </Route>
                            <Route element={<PermissionRoute
                              permission="invoicing.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/invoicing/items" element={<InvoicingItems />} />
                              <Route path="/invoicing" element={<InvoicingList />} />
                              <Route path="/invoicing/invoices/:uuid" element={<InvoiceDetail />} />
                            </Route>

                            {/* Payments carry their own permission: recording money is a
                                different job from building an invoice. */}
                            <Route element={<PermissionRoute
                              permission="invoicing.payments"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/invoicing/payments" element={<InvoicingPayments />} />
                            </Route>

                            {/* Cafeteria POS administration (spec-02 §7). Each route is
                                gated on the permission its own tab actually needs, not on
                                a single umbrella one: a manager who may read the day
                                totals has no business editing the catalog, and the server
                                enforces exactly that split per endpoint. */}
                            <Route element={<PermissionRoute
                              permission="pos.catalog"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/pos/catalog" element={<PosCatalog />} />
                              <Route path="/pos/config" element={<PosConfig />} />
                            </Route>
                            <Route element={<PermissionRoute
                              permission="pos.restrictions"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/pos/restrictions" element={<PosRestrictions />} />
                            </Route>
                            <Route element={<PermissionRoute
                              permission="pos.terminals"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/pos/terminals" element={<PosTerminals />} />
                            </Route>
                            <Route element={<PermissionRoute
                              permission="pos.cash_count"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/pos/day-closes" element={<PosDayCloses />} />
                            </Route>
                            {/* pos.report_persons is NOT required here. The report opens
                                without it and the per-resident breakdown simply is not in
                                the payload — the server strips it, so gating the whole
                                page on it would hide the totals from everyone. */}
                            <Route element={<PermissionRoute
                              permission="pos.reports"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/pos/reports" element={<PosReports />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission="tickets.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/tickets" element={<TicketsManagement />} />
                              <Route path="/tickets-new" element={<TicketsManagement />} />
                            </Route>

                            {/* Inbound email quarantine review (spec 20260625-ticket-email-reply, T036).
                                CARO-only (company 1) since 2026-09-30: an unmatched mail has no
                                tenant, and behind tickets.view alone every customer read every
                                other customer's quarantined mail. The API repeats the gate. */}
                            <Route element={<CompanyPermissionRoute
                              permission="tickets.view"
                              allowedCompanyIds={1}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/tickets/quarantine" element={<QuarantineQueue />} />
                            </Route>

                            {/* Alarms Dashboard - Admin only */}
                            <Route path="/alarms" element={<AlarmsDashboard />} />
                            <Route path="/alarms/:id" element={<AlarmDetails />} />

                            {/* Alarm Flow Configuration */}
                            <Route path="/alarm-flows" element={<AlarmFlows />} />
                            <Route path="/alarm-flows/new" element={<AlarmFlowEditor />} />
                            <Route path="/alarm-flows/bindings" element={<AlarmFlowBindings />} />
                            <Route path="/alarm-flows/:id" element={<AlarmFlowEditor />} />

                            <Route element={<PermissionRoute
                              permission="camera_requests.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/camera-requests" element={<CameraRequests />} />
                              <Route path="/camera-requests/:id" element={<RequestDetail />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission="field_interventions.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/field-interventions" element={<FieldInterventions />} />
                              <Route path="/field-interventions/new" element={<CreateIntervention />} />
                              <Route path="/field-interventions/:id" element={<InterventionDetail />} />
                            </Route>

                            {/* Shop / device catalog (spec 20260702-repair-estimates-catalog).
                                Read-only Shop for catalog.view; catalog.manage unlocks the
                                admin tabs inside the page. */}
                            <Route element={<PermissionRoute
                              permission="catalog.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/shop" element={<Shop />} />
                            </Route>

                            {/* Catalog / product management — deep-links to the
                                Shop page's management tab. Gated by catalog.manage. */}
                            <Route element={<PermissionRoute
                              permission="catalog.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/catalog-management" element={<Shop initialTab={1} />} />
                            </Route>

                            {/* Estimates overview — cross-ticket list + mark invoiced
                                (2026-07-17). Read gated by estimates.view. */}
                            <Route element={<PermissionRoute
                              permission="estimates.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/estimates" element={<EstimatesOverview />} />
                            </Route>

                            {/* User management. ANY of the three keys opens the page, which
                                is what PAGE_REGISTRY has always declared for /users
                                (pageRegistry.ts) -- the guard used to name
                                `users.permissions.manage` alone, so someone granted
                                `users.manage` was bounced from a page the permission matrix
                                had just told an administrator they had opened.

                                What each key reaches is decided INSIDE the page:
                                  users.view              -> the roster, read-only
                                  users.manage            -> create / invite / deactivate / role
                                  users.permissions.manage-> the permission matrix + impersonation

                                Spec: specs/20260826-company-user-management/spec.md LD-3. */}
                            <Route element={<PermissionRoute
                              permission={['users.view', 'users.manage', 'users.permissions.manage']}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/users" element={<UserManagement />} />
                            </Route>

                            {/* Documentclassificatie (spec 20260902-documentclassificatie, T-5a.35,
                                PD-9). ANY of the seven keys the PAGE_REGISTRY row for /documents
                                declares opens the landing page: `documents.classify` for the work
                                list, the six `documents.read.<topic>` keys for the tiles. Reading a
                                FILE is never a key — that is the engine's answer (D-14); this guard
                                only decides who sees the page. The three admin pages (grants,
                                exclusions, archive) are 5b/5c and carry their own PROTECTED keys. */}
                            <Route element={<PermissionRoute
                              permission={['documents.classify', 'documents.read.medical', 'documents.read.financial', 'documents.read.legal', 'documents.read.technical', 'documents.read.housing', 'documents.read.hr']}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/documents" element={<Documents />} />
                            </Route>

                            {/* Documentclassificatie 5b (T-5b.19). Two PROTECTED keys, one page each:
                                `documents.access_admin` → grants (manual, strikt with the typed name,
                                72-hour emergency, recertification, the soak report);
                                `documents.exclusion_admin` → exclusions. The API refuses an account holding
                                both (422 INCOMPATIBLE_PERMISSIONS), so nobody legitimately reaches both. */}
                            <Route element={<PermissionRoute
                              permission="documents.access_admin"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/documents/grants" element={<DocumentsGrants />} />
                            </Route>
                            <Route element={<PermissionRoute
                              permission="documents.exclusion_admin"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/documents/exclusions" element={<DocumentsExclusions />} />
                            </Route>
                            {/* 5c — the archiving page (spec §9, D-12): `documents.retention` (PROTECTED).
                                Proposals, legal hold, audited batch deletion; the API gates every
                                /documents/archive/* route on the same key. */}
                            <Route element={<PermissionRoute
                              permission="documents.retention"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/documents/archive" element={<DocumentsArchive />} />
                            </Route>

                            {/* Deep-link to the Templates tab, same pattern as
                                /catalog-management -> Shop. A real route rather than a
                                query string so the nav can highlight it and the
                                permission-templates PAGE_REGISTRY entry has a route that
                                actually exists.

                                Keeps its OWN gate: editing the sets every colleague is
                                stamped with is not the same right as adding a colleague. */}
                            <Route element={<PermissionRoute
                              permission={['templates.manage', 'templates.edit']}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/permission-templates" element={<UserManagement initialTab="templates" />} />
                            </Route>

                            {/* Cross-tenant: lists orphan locations across every customer and
                                assigns tenancy. Guarded on locations.tenant_repair, which is
                                PROTECTED so company_admin cannot inherit it.

                                NOTE: this and /audit below used to sit INSIDE the
                                users.permissions.manage guard above -- an unclosed <Route>,
                                not a decision. Both therefore demanded that key on top of
                                their own, so an auditor holding audit.view and nothing else
                                could not open the audit trail the key exists for. */}
                            <Route element={<PermissionRoute permission="locations.tenant_repair" />}>
                              <Route path="/admin/location-tenancy" element={<LocationTenancy />} />
                            </Route>

                            {/* NIS2 audit reporting. `audit.view` is PROTECTED: the trail
                                names which member of staff opened which resident record, so
                                company_admin must not inherit it via FULL_ACCESS_ROLES. The
                                export is a second, separate key checked inside the page. */}
                            <Route element={<PermissionRoute permission="audit.view" />}>
                              <Route path="/audit" element={<AuditTrail />} />
                            </Route>

                            {/* Persons Management */}
                            <Route element={<PermissionRoute
                              permission="persons.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/persons" element={<PersonsManagement />} />
                            </Route>

                            {/* Person Type Management (admin) */}
                            <Route element={<PermissionRoute
                              permission="persontypes.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/person-type-management" element={<PersonTypeManagement />} />
                            </Route>

                            {/* Admin-only routes - admin check is handled within the components */}
                            {/* /company-management is CARO-only (spec-02 LD-CM-SPLIT). A
                                customer admin who lands here is redirected to /organisation
                                by the component, so old bookmarks keep working. */}
                            <Route path="/company-management" element={<CompanyManagement />} />
                            <Route path="/organisation" element={<Organisation />} />
                            <Route path="/admin/onboarding" element={<OnboardingAdmin />} />
                            <Route path="/service-management" element={<ServiceManagement />} />
                            <Route path="/device-type-management" element={<DeviceTypeManagement />} />
                            <Route path="/device-linking" element={<DeviceLinking />} />
                            <Route path="/baseip-management" element={<BaseIPManagement />} />
                            <Route path="/enocean-buttons" element={<EnOceanButtonManagement />} />
                            <Route path="/enocean-import" element={<EnOceanButtonImport />} />
                            <Route path="/location-data-import" element={<LocationDataImport />} />
                            <Route path="/unit-audit" element={<UnitAudit />} />
                            <Route path="/qbus-output-types" element={<QbusOutputTypeManagement />} />
                            <Route path="/qbus-prefixes" element={<QbusPrefixes />} />
                            <Route path="/automations" element={<Automations />} />
                            <Route path="/qbus-friendly-names" element={<QbusFriendlyNames />} />
                            <Route path="/qbus-monitor" element={<QbusMonitor />} />
                            <Route path="/telephony" element={<TelephonyManagement />} />
                            <Route path="/tablet-permissions" element={<TabletPermissions />} />
                            <Route path="/eink-designer" element={<EinkDesigner />} />

                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/integration-management" element={<IntegrationManagement />} />
                              <Route path="/admin/integrations/emergency-services" element={<EmergencyServicesManagement />} />
                              <Route path="/admin/integrations/twon/commanders" element={<CommanderList />} />
                            </Route>

                            {/* Firmware management is CARO-only (company 1).
                              *
                              * Spec: specs/20260825-overzicht-per-firmwareversie (LD-1).
                              *
                              * BREAKING CHANGE, AND DELIBERATE. Until now this route carried only a
                              * permission guard, and `company_admin` short-circuits hasPermission
                              * through FULL_ACCESS_ROLES — so every customer's own administrator
                              * could open it. The page now carries fleet-wide device counts spanning
                              * every tenant, and its click-through target (/devices, just above) has
                              * been company-1 only all along. Customers who can reach this page today
                              * lose it; that is the correct direction, not a regression.
                              *
                              * The gate is repeated server-side on /api/devices/version-rollup. A
                              * React route guard is not an access control — the API answers any valid
                              * token regardless of what this file renders.
                              */}
                            <Route element={<CompanyPermissionRoute
                              permission="firmware.view"
                              allowedCompanyIds={1}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/firmware-management" element={<FirmwareManagement />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/notification-management" element={<NotificationManagement />} />
                            </Route>

                            {/* <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/translations" element={<TranslationManagement />} />
                            </Route> */}

                            <Route path="/settings" element={<Settings />} />
                            <Route path="/notifications" element={<Notifications />} />

                            {/* /monitoring — gated on monitoring.view.
                                Spec: specs/20260805-api-observability/ §2.7.
                                It sat in the plain authenticated block, so only the sidebar
                                link was adminOnly: any authenticated user who typed the URL
                                got the full fleet dashboard. The permission already existed
                                in @caro/shared/constants/permissions — it was simply never
                                applied to the route. */}
                            <Route element={<PermissionRoute
                              permission="monitoring.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/monitoring" element={<MonitoringDashboard />} />
                              {/* Observability feedback loop — same monitoring.view gate.
                                  The write on that page (marking a fix as shipped) is gated
                                  separately on monitoring.manage, in the component and again
                                  server-side. */}
                              <Route path="/observability" element={<Observability />} />
                              <Route path="/troubleshooting" element={<Troubleshooting />} />
                            </Route>

                            {/* Database server health (2026-09-18). CARO-only (company 1) the
                                same way /firmware-management is: the key is NOT PROTECTED so
                                CARO admins hold it automatically, and the company gate keeps a
                                customer's company_admin out. The API repeats the company check
                                inside /api/system/db-health — this guard is not an access
                                control on its own. */}
                            <Route element={<CompanyPermissionRoute
                              permission="system.db_health.view"
                              allowedCompanyIds={1}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/database-health" element={<DatabaseHealth />} />
                            </Route>

                            {/* TODO(observability): /hub-status and /hub-location-status are
                                unguarded in exactly the same way. They are deliberately left
                                as-is here — gating them would revoke access for anyone
                                currently using them without monitoring.view, which is an
                                operational decision, not a code cleanup. Raise it before
                                changing. */}
                            <Route path="/hub-status" element={<HubStatus />} />
                            <Route path="/hub-location-status" element={<HubLocationStatus />} />
                            {/* Knowledge Base — auth-gated help center (any role).
                                Home + page-detail + status are ungated reads;
                                the authoring console is gated on knowledge_base.manage.
                                Spec: specs/20260706-portal-knowledge-base/ Phase 1. */}
                            <Route path="/help" element={<KnowledgeBaseHome />} />
                            <Route path="/help/p/:slug" element={<KbPage />} />
                            <Route path="/help/status" element={<KbStatus />} />
                            <Route element={<PermissionRoute
                              permission="knowledge_base.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/help/admin" element={<KbAdmin />} />
                            </Route>
                            <Route path="/release-notes/:version" element={<ReleaseNotesViewer />} />

                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/developer-info" element={<DeveloperInfo />} />
                            </Route>

                            {/* Indoor Mapping Admin - Admin only */}
                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/indoor-mapping" element={<IndoorMapping />} />
                              <Route path="/indoor-navigation-preview" element={<IndoorNavigationPreview />} />
                            </Route>

                            </Route>

                          {/* Fullscreen presentations - outside Layout for no navbar/header/footer */}
                          <Route element={<PermissionRoute
                            permission="system.admin"
                            fallbackPath="/permission-denied"
                          />}>
                            <Route path="/business-update" element={<BusinessUpdate />} />
                            <Route path="/sales-presentation" element={<SalesPresentation />} />
                          </Route>
                          </Route>

                          {/* Catch-all - redirect to login */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </RouteErrorBoundary>
                        </Suspense>
                      </Router>
                      </ErrorBoundary>
                      </ModulesProvider>
                      </CommandRegistryProvider>
                      </SiteFilterProvider>
                    </NavigationProvider>
                  </DeviceProvider>
                </FileProvider>
              </PermissionProvider>
            </AuthProvider>
          </SnackbarProvider>
        </LocalizationProvider>
      </CombinedThemeProvider>
    </QueryClientProvider>
  );
}

export default App;